import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import {
  CharterAdministratorDto,
  CharterHolderDto,
  CharterLegalNature,
} from '@interfaces/corporate-charter/responses/corporate-charter.response';
import { VaultDatePipe } from '@shared/pipes/vault-date.pipe';
import { CharterAdministratorItemComponent } from '@features/dashboard/company/corporate-charter/components/charter-administrator-item/charter-administrator-item.component';
import { CharterHistoryListComponent } from '@features/dashboard/company/corporate-charter/components/charter-history-list/charter-history-list.component';
import { CharterPersonDocumentComponent } from '@features/dashboard/company/corporate-charter/components/charter-person-document/charter-person-document.component';
import { CharterProvenanceComponent } from '@features/dashboard/company/corporate-charter/components/charter-provenance/charter-provenance.component';
import {
  holderHistoryRows,
  lastSeen,
} from '@features/dashboard/company/corporate-charter/corporate-charter-provenance.util';
import { isCorporation } from '@features/dashboard/company/corporate-charter/corporate-charter.util';

/**
 * "Ex-sócios e ex-administradores": quem aparece em ato anterior e não está no vigente (`formerHolders` e
 * `governance.formerAdministrators`). Cada pessoa mostra o ato em que saiu (ou o último que a listava), a última
 * participação conhecida e o histórico completo com a procedência de cada ato.
 */
@Component({
  selector: 'app-charter-former-card',
  imports: [
    VaultDatePipe,
    CharterAdministratorItemComponent,
    CharterHistoryListComponent,
    CharterPersonDocumentComponent,
    CharterProvenanceComponent,
  ],
  templateUrl: './charter-former-card.component.html',
  styleUrl: './charter-former-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CharterFormerCardComponent {
  readonly formerHolders = input<CharterHolderDto[]>([]);
  readonly formerAdministrators = input<CharterAdministratorDto[]>([]);
  readonly legalNature = input<CharterLegalNature | null>(null);
  readonly unitKind = input<'quotas' | 'shares'>('quotas');
  readonly currency = input('BRL');

  protected readonly expanded = signal<ReadonlySet<string>>(new Set());

  protected readonly isCorporation = computed(
    () => isCorporation(this.legalNature()) || this.unitKind() === 'shares'
  );

  protected readonly holders = computed(() =>
    this.formerHolders().map(holder => {
      const history = holderHistoryRows(holder.history ?? [], this.unitKind(), this.currency());
      const lastListed = [...history].reverse().find(row => row.tone === 'listed');
      return {
        holder,
        history,
        last: lastSeen(holder.history ?? []),
        lastValues: lastListed?.values ?? null,
      };
    })
  );

  protected toggle(personId: string): void {
    this.expanded.update(set => {
      const next = new Set(set);
      if (next.has(personId)) next.delete(personId);
      else next.add(personId);
      return next;
    });
  }
}
